import {getStepsFromPixels} from 'platform/foundation';
import {css, useTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {useRef, type KeyboardEvent, type PointerEvent} from 'react';

import {clamp, isNil} from 'ramda';

import i18n from '@dms/i18n';

import {useOnMount} from 'shared/web';

import {ACTIVITY_SIDEBAR_WIDTH} from '../constants/ACTIVITY_SIDEBAR_WIDTH';
import {getActivitySidebarWidthCss} from '../utils/getActivitySidebarWidthCss';

interface ActivitySidebarResizeHandleProps {
  width: number;
  min: number;
  max: number;
  onChange: (width: number) => void;
  onReset: () => void;
}

const HANDLE_HIT_AREA_SIZE = 3;
const HANDLE_LINE_SIZE = 1;
const HANDLE_Z_INDEX = 1;

export function ActivitySidebarResizeHandle(props: ActivitySidebarResizeHandleProps) {
  const theme = useTheme();
  const movedRef = useRef(false);
  const previewWidthRef = useRef<number | undefined>(undefined);
  const panelRef = useRef<HTMLElement | null>(null);
  const panelRightRef = useRef(0);
  const settleFrameRef = useRef<number | undefined>(undefined);
  const widthRef = useRef(props.width);
  widthRef.current = props.width;

  const cancelSettle = () => {
    if (!isNil(settleFrameRef.current)) {
      cancelAnimationFrame(settleFrameRef.current);
    }
  };

  useOnMount(undefined, cancelSettle);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    cancelSettle();
    movedRef.current = false;
    previewWidthRef.current = undefined;
    panelRef.current = event.currentTarget.parentElement;
    panelRightRef.current = panelRef.current?.getBoundingClientRect().right ?? 0;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const panel = event.currentTarget.parentElement;
    if (isNil(panel) || !event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }

    const steps = getStepsFromPixels(panelRightRef.current - event.clientX);
    if (isNil(steps)) {
      return;
    }

    const previewWidth = clamp(props.min, props.max, steps);
    movedRef.current = true;
    previewWidthRef.current = previewWidth;
    panel.style.width = getActivitySidebarWidthCss(previewWidth);
  };

  const handleLostPointerCapture = () => {
    const panel = panelRef.current;
    if (movedRef.current && !isNil(previewWidthRef.current)) {
      props.onChange(previewWidthRef.current);
      settleFrameRef.current = requestAnimationFrame(() => {
        settleFrameRef.current = requestAnimationFrame(() => {
          panel?.style.setProperty('width', getActivitySidebarWidthCss(widthRef.current));
        });
      });
    }

    movedRef.current = false;
    previewWidthRef.current = undefined;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    match(event.key)
      .with('ArrowLeft', () => {
        event.preventDefault();
        props.onChange(props.width + ACTIVITY_SIDEBAR_WIDTH.KEYBOARD_STEP);
      })
      .with('ArrowRight', () => {
        event.preventDefault();
        props.onChange(props.width - ACTIVITY_SIDEBAR_WIDTH.KEYBOARD_STEP);
      })
      .with('Home', () => {
        event.preventDefault();
        props.onChange(props.min);
      })
      .with('End', () => {
        event.preventDefault();
        props.onChange(props.max);
      })
      .otherwise(() => undefined);
  };

  return (
    <div
      role="separator"
      aria-label={i18n.t('entity.activityPanel.resizeHandle')}
      aria-orientation="vertical"
      aria-valuemin={props.min}
      aria-valuemax={props.max}
      aria-valuenow={props.width}
      aria-valuetext={`${props.width * ACTIVITY_SIDEBAR_WIDTH.STEP_PX}px`}
      tabIndex={0}
      onDoubleClick={props.onReset}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onLostPointerCapture={handleLostPointerCapture}
      onKeyDown={handleKeyDown}
      data-testid="activitySidebarResizeHandle"
      css={css`
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        z-index: ${HANDLE_Z_INDEX};
        width: ${theme.getSize(HANDLE_HIT_AREA_SIZE)};
        cursor: col-resize;
        touch-action: none;
        user-select: none;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: ${theme.getSize(HANDLE_LINE_SIZE)};
          background-color: ${theme.colors.general.separator};
        }

        &:hover::after,
        &:focus-visible::after {
          background-color: ${theme.colors.palettes.blue['60']['100']};
        }

        &:focus-visible {
          outline: ${theme.getSize(HANDLE_LINE_SIZE)} solid
            ${theme.colors.palettes.blue['60']['100']};
        }
      `}
    />
  );
}
